import { Injectable, inject } from '@angular/core';
import { Command, UndoCommand } from '../models/commands/command';
import { first, tap } from 'rxjs/operators';
import { TreeViewService } from './tree-view.service';

@Injectable({
    providedIn: 'root'
})
export class CommandHistoryService {
    private treeview = inject(TreeViewService);

    private commands: UndoCommand<unknown>[] = [];
    // 0 = empty history
    private index: number = 0;

    push(command: Command<unknown>): void {
        if (this.isUndoCommand(command)) {
            this.next().forEach(c => c.destroy());
            this.commands = [...this.history(), command];
            this.index++;
        } else {
            this.clear();
        }
    }

    undo(): void {
        if (this.index == 0) {
            throw new Error('Undo impossible: pile de commandes vide');
        }
        this.commands[this.index - 1]
            .undo()
            .pipe(
                first(),
                tap(() => this.index--)
            )
            .subscribe(() => {
                this.treeview.refresh();
            });
    }

    canUndo(): boolean {
        return this.index > 0;
    }

    canRedo(): boolean {
        return this.index < this.commands.length;
    }

    redo(): void {
        if (this.index >= this.commands.length) {
            throw new Error('Redo impossible: fin de pile atteinte');
        }
        this.commands[this.index]
            .do()
            .pipe(
                first(),
                tap(() => this.index++)
            )
            .subscribe(() => {
                this.treeview.refresh();
            });
    }

    clear(): void {
        this.commands.forEach(c => c.destroy());
        this.commands = [];
        this.index = 0;
    }

    private history(): UndoCommand<unknown>[] {
        return this.commands.slice(0, this.index);
    }

    private next(): UndoCommand<unknown>[] {
        return this.commands.slice(this.index);
    }

    private isUndoCommand(command: Command<unknown> | UndoCommand<unknown>): command is UndoCommand<unknown> {
        return (<UndoCommand<unknown>>command).undo !== undefined;
    }
}
